import api from './api';

export interface LiveEventDto {
  id: number;
  learningObjectId: number;
  learningObjectTitle: string;
  platform: string;
  platformId: string | null;
  scheduledAt: string;
  timezone: string;
  status: string;
  maxAttendees: number | null;
  registrationCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface LiveEventRegistrationDto {
  id: number;
  liveEventId: number;
  userId: number;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userFullName: string;
  registeredAt: string;
  attendedAt: string | null;
  attendanceDurationMinutes: number | null;
  /** Beacon timed-delivery URL captured at enrollment. Null for non-Beacon events or when Beacon was unavailable. */
  launchUrl: string | null;
  /** Beacon 360 numeric order id captured at enrollment. Used by support to look the order up in Beacon for troubleshooting. */
  beaconOrderId: number | null;
}

export interface PagedLiveEventRegistrations {
  items: LiveEventRegistrationDto[];
  totalCount: number;
  page: number;
  limit: number;
}

export interface CreateLiveEventRequest {
  platform: string;
  platformId?: string | null;
  scheduledAt: string;
  timezone: string;
  maxAttendees?: number | null;
}

export interface UpdateLiveEventRequest {
  platform?: string | null;
  platformId?: string | null;
  scheduledAt?: string | null;
  timezone?: string | null;
  status?: string | null;
  maxAttendees?: number | null;
}

export interface SendLiveEventInviteRequest {
  email: string;
  message?: string;
}

export interface CalendarIcsDownload {
  blob: Blob;
  filename: string;
}

/**
 * MVP-C: per-recipient outcome of a date-change blast. SendGrid acceptance
 * indicates the message was queued for delivery — it does NOT confirm inbox
 * landing. The confirmation page surfaces this signal verbatim and labels
 * it accordingly.
 */
export interface LiveEventDateChangeRecipient {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  sendGridAccepted: boolean;
  errorMessage: string | null;
}

export interface LiveEventDateChangeResult {
  liveEventId: number;
  courseTitle: string;
  priorScheduledAtUtc: string;
  newScheduledAtUtc: string;
  recipientCount: number;
  successCount: number;
  failureCount: number;
  recipients: LiveEventDateChangeRecipient[];
}

export const liveEventApi = {
  getLiveEvents: async (learningObjectId: number): Promise<LiveEventDto[]> => {
    const response = await api.get(`/learning-objects/${learningObjectId}/live-events?limit=100`);
    return response.data.items ?? response.data;
  },

  getLiveEvent: async (id: number): Promise<LiveEventDto> => {
    const response = await api.get(`/live-events/${id}`);
    return response.data;
  },

  createLiveEvent: async (learningObjectId: number, data: CreateLiveEventRequest): Promise<LiveEventDto> => {
    const response = await api.post(`/learning-objects/${learningObjectId}/live-events`, data);
    return response.data;
  },

  updateLiveEvent: async (id: number, data: UpdateLiveEventRequest): Promise<LiveEventDto> => {
    const response = await api.put(`/live-events/${id}`, data);
    return response.data;
  },

  deleteLiveEvent: async (id: number): Promise<void> => {
    await api.delete(`/live-events/${id}`);
  },

  /**
   * Self-service enroll the current user in a live event.
   * Returns 201 on new registration, 200 on idempotent re-enroll.
   * On 502 BEACON_UNAVAILABLE the caller should surface a retry-friendly error.
   * On 409 the response carries a contract-resolution errorCode (NO_ACTIVE_CONTRACT, etc.).
   */
  enroll: async (liveEventId: number): Promise<LiveEventRegistrationDto> => {
    const response = await api.post<LiveEventRegistrationDto>(`/live-events/${liveEventId}/enroll`);
    return response.data;
  },

  /**
   * Re-sends the live-event confirmation email with the stored launch URL.
   * Returns 204 on success. Server returns 410 Gone after the event ends — the
   * Resend button is hidden in that case but the server enforces it independently.
   */
  resendLaunchLink: async (liveEventId: number): Promise<void> => {
    await api.post(`/live-events/${liveEventId}/resend-launch-link`);
  },

  /**
   * Fetch the .ics file for an upcoming live event. Returns the blob plus the
   * server-assigned filename so callers can trigger a browser download without
   * re-deriving the name from the course title.
   */
  downloadIcs: async (eventId: number): Promise<CalendarIcsDownload> => {
    const response = await api.get(`/live-events/${eventId}/calendar.ics`, {
      responseType: 'blob',
    });
    return {
      blob: response.data,
      filename: parseFilenameFromContentDisposition(response.headers['content-disposition'])
        ?? `live-event-${eventId}.ics`,
    };
  },

  /**
   * Email a calendar invite with .ics attached to an arbitrary recipient.
   * Resolves on 200. Axios throws for the error statuses the endpoint defines
   * (400 invalid email, 404 event missing/past, 429 rate-limited, 502 send failed).
   */
  sendEmailInvite: async (eventId: number, payload: SendLiveEventInviteRequest): Promise<void> => {
    await api.post(`/live-events/${eventId}/email-invite`, payload);
  },

  /**
   * Super-admin roster: paged list of registrations for a single live event.
   * Server returns name/email plus the BeaconOrderId, which support uses to
   * look the matching Beacon order up for troubleshooting.
   */
  getRegistrations: async (
    eventId: number,
    page = 1,
    limit = 25,
  ): Promise<PagedLiveEventRegistrations> => {
    const response = await api.get<PagedLiveEventRegistrations>(
      `/live-events/${eventId}/registrations`,
      { params: { page, limit } },
    );
    return response.data;
  },

  /**
   * Super-admin: remove a user from a live event. The PE-side delete also nulls
   * out BeaconTimedUrl + ContractId on the matching progress row so the row
   * stops counting against the contract's webinar usage. Beacon is NOT contacted
   * — the operator must delete the Beacon order separately as a runbook step.
   */
  unregister: async (eventId: number, userId: number): Promise<void> => {
    await api.delete(`/live-events/${eventId}/register/${userId}`);
  },

  /**
   * MVP-C: returns the count of active enrollees for a live event. Called by
   * the super-admin live-event edit form right after a successful date update
   * to decide whether to show the date-change notification modal.
   */
  getActiveRegistrationCount: async (eventId: number): Promise<number> => {
    const response = await api.get<{ count: number }>(`/live-events/${eventId}/active-registration-count`);
    return response.data.count;
  },

  /**
   * MVP-C: fans out the date-change notification email to every active
   * enrollee. Returns per-recipient SendGrid acceptance for the confirmation
   * page. The date itself must already be saved through updateLiveEvent;
   * this endpoint is a separate step so admins can also choose not to notify.
   */
  notifyDateChange: async (
    eventId: number,
    priorScheduledAtUtc: string,
  ): Promise<LiveEventDateChangeResult> => {
    const response = await api.post<LiveEventDateChangeResult>(
      `/live-events/${eventId}/notify-date-change`,
      { priorScheduledAtUtc },
    );
    return response.data;
  },
};

/**
 * Extracts the filename from a Content-Disposition header. Handles both
 * plain `filename="..."` and RFC 5987 `filename*=UTF-8''...` forms that
 * ASP.NET Core emits.
 */
function parseFilenameFromContentDisposition(header: string | undefined): string | undefined {
  if (!header) return undefined;

  // RFC 5987: filename*=UTF-8''percent-encoded
  const extendedMatch = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (extendedMatch?.[1]) {
    try {
      return decodeURIComponent(extendedMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to plain filename */
    }
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch?.[1]?.trim();
}
